import React, { useMemo } from 'react';
import { Alert, Button, Card, Form, Input, Space, Typography, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Api from '../../Network';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type AgreementClause = {
  title: string;
  body: string;
};

type AgreementTemplate = {
  version: number;
  locale: string;
  title: string;
  intro_template: string;
  party_one: string;
  party_two_template: string;
  clauses: AgreementClause[];
  updated_at?: string;
};

type ApiResponse = {
  data?: AgreementTemplate;
};

const AccountAgreementSettings: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AgreementTemplate>();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['/api/admin/account-agreement'],
    queryFn: async () => {
      const response = (await Api.get('/api/admin/account-agreement')) as
        | ApiResponse
        | AgreementTemplate;
      return ((response as ApiResponse)?.data ?? response) as AgreementTemplate;
    },
  });

  const formKey = data
    ? `agreement-v${data.version}-${data.updated_at ?? 'loaded'}`
    : 'agreement-loading';

  const initialValues = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return {
      title: data.title,
      intro_template: data.intro_template,
      party_one: data.party_one,
      party_two_template: data.party_two_template,
      clauses: Array.isArray(data.clauses) ? data.clauses : [],
    };
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: AgreementTemplate) => Api.put('/api/admin/account-agreement', payload),
    onSuccess: () => {
      message.success(t('accountAgreement.saveSuccess'));
      refetch();
    },
    onError: () => {
      message.error(t('common.error'));
    },
  });

  const onFinish = (values: AgreementTemplate) => {
    saveMutation.mutate({
      ...values,
      locale: data?.locale || 'ar',
      clauses: (values.clauses || []).map((clause) => ({
        title: clause.title?.trim() || '',
        body: clause.body?.trim() || '',
      })),
    });
  };

  return (
    <AnimatedContainer className="account-agreement-page" variant="fadeUp">
      <Card loading={isLoading} className="account-agreement-card" dir="rtl">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="account-agreement-header">
            <Title level={3}>{t('accountAgreement.title')}</Title>
            <Paragraph type="secondary">{t('accountAgreement.subtitle')}</Paragraph>
            {data?.version ? (
              <Text type="secondary">
                {t('accountAgreement.currentVersion')}: {data.version}
              </Text>
            ) : null}
          </div>

          <Alert
            type="info"
            showIcon
            message={t('accountAgreement.placeholdersTitle')}
            description={
              <span>
                {
                  '{{day_name}} {{date_d}} {{date_m}} {{date_y}} {{company_name}} {{cr_number}} {{headquarters}} {{signer_name}} {{signer_title}}'
                }
              </span>
            }
          />

          {initialValues ? (
            <Form
              key={formKey}
              form={form}
              layout="vertical"
              initialValues={initialValues}
              onFinish={onFinish}
              className="account-agreement-form"
            >
              <div className="agreement-preview-card">
                <Form.Item
                  name="title"
                  rules={[{ required: true, message: t('accountAgreement.validation.title') }]}
                  className="agreement-title-field"
                >
                  <Input
                    className="agreement-title-input"
                    placeholder={t('accountAgreement.fields.title')}
                  />
                </Form.Item>

                <div className="agreement-divider" />

                <Form.Item
                  name="intro_template"
                  rules={[{ required: true, message: t('accountAgreement.validation.intro') }]}
                  className="agreement-body-field"
                >
                  <TextArea
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    className="agreement-body-input"
                    placeholder={t('accountAgreement.fields.intro')}
                  />
                </Form.Item>

                <Form.Item
                  name="party_one"
                  rules={[{ required: true, message: t('accountAgreement.validation.partyOne') }]}
                  className="agreement-party-field"
                >
                  <TextArea
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    className="agreement-party-input"
                    placeholder={t('accountAgreement.fields.partyOne')}
                  />
                </Form.Item>

                <Form.Item
                  name="party_two_template"
                  rules={[{ required: true, message: t('accountAgreement.validation.partyTwo') }]}
                  className="agreement-party-field"
                >
                  <TextArea
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    className="agreement-party-input"
                    placeholder={t('accountAgreement.fields.partyTwo')}
                  />
                </Form.Item>

                <p className="agreement-preamble">{t('accountAgreement.preamble')}</p>

                <div className="agreement-clauses-section">
                  <Title level={5} className="agreement-clauses-heading">
                    {t('accountAgreement.fields.clauses')} ({initialValues.clauses.length})
                  </Title>

                  <Form.List name="clauses">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }, index) => (
                          <div key={key} className="agreement-clause-block">
                            <div className="agreement-clause-header">
                              <Text className="agreement-clause-number">
                                {t('accountAgreement.clauseNumber', { number: index + 1 })}
                              </Text>
                              <Button
                                danger
                                type="text"
                                size="small"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                              >
                                {t('accountAgreement.removeClause')}
                              </Button>
                            </div>

                            <Form.Item
                              {...restField}
                              name={[name, 'title']}
                              rules={[
                                {
                                  required: true,
                                  message: t('accountAgreement.validation.clauseTitle'),
                                },
                              ]}
                              className="agreement-clause-title-field"
                            >
                              <Input
                                className="agreement-clause-title-input"
                                placeholder={t('accountAgreement.fields.clauseTitle')}
                              />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              name={[name, 'body']}
                              rules={[
                                {
                                  required: true,
                                  message: t('accountAgreement.validation.clauseBody'),
                                },
                              ]}
                              className="agreement-clause-body-field"
                            >
                              <TextArea
                                autoSize={{ minRows: 4, maxRows: 16 }}
                                className="agreement-clause-body-input"
                                placeholder={t('accountAgreement.fields.clauseBody')}
                              />
                            </Form.Item>
                          </div>
                        ))}

                        <Button
                          type="dashed"
                          onClick={() => add({ title: '', body: '' })}
                          block
                          icon={<PlusOutlined />}
                          className="agreement-add-clause-btn"
                        >
                          {t('accountAgreement.addClause')}
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>
              </div>

              <Form.Item className="agreement-save-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saveMutation.isPending}
                  size="large"
                >
                  {t('accountAgreement.save')}
                </Button>
              </Form.Item>
            </Form>
          ) : null}
        </Space>
      </Card>
    </AnimatedContainer>
  );
};

export default AccountAgreementSettings;
